import React, { useRef, Fragment, useMemo, useState } from "react";
import { UnfoldMore } from "@material-ui/icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getExpandedRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import StatusTag from "../../StatusTag";
import IssuesBadge from "../../IssuesBadge";
import WarningsBadge from "../../WarningsBadge";
import ClipboardTooltip from "../../EspIdTooltip";

export default function MedicalEncounterClaimDetailsTable(props) {
  const tableContainerRef = useRef();

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("esp_id", {
      header: "ESP ID",
      enableSorting: false,
      cell: ({ renderValue }) => <ClipboardTooltip value={renderValue()} />,
    }),
    columnHelper.accessor("manufacturer", {
      header: "Mfr",
      enableSorting: false,
      cell: ({ renderValue }) => <div className="t--sm">{renderValue()}</div>,
    }),
    columnHelper.accessor("ndc", {
      header: "NDC",
      enableSorting: false,
      cell: ({ row, getValue }) => {
        return (
          <div>
            <div className="t--sm t--500">{row.original.product_name}</div>
            <div className="t--sm t--subtitle">{getValue()}</div>
          </div>
        );
      },
    }),
    columnHelper.accessor("quantity", {
      header: "Quantity",
      cell: ({ renderValue }) => <div className="t--sm">{renderValue()}</div>,
    }),
    columnHelper.accessor("service_provider_id", {
      header: "Service Provider ID",
      enableSorting: false,
    }),
    columnHelper.accessor("submitted_id_340b", {
      header: "340B ID",
      enableSorting: false,
      cell: ({ renderValue }) => <div className="t--sm">{renderValue()}</div>,
    }),
    columnHelper.accessor((row) => row.status, {
      id: "result",
      header: "Result",
      enableSorting: false,
      cell: ({ getValue }) => {
        return <StatusTag status={getValue()} type="claim" />;
      },
    }),
    columnHelper.accessor("issues", {
      header: "Issues",
      cell: ({ getValue }) => <IssuesBadge issues={getValue()} />,
    }),
    columnHelper.accessor("warnings", {
      header: "Warnings",
      cell: ({ getValue }) => <WarningsBadge warnings={getValue()} />,
    }),
    columnHelper.accessor("submission_date", {
      header: "Date Submitted",
      cell: ({ renderValue }) => <div className="t--sm">{renderValue()}</div>,
    }),
  ];

  const claims = useMemo(() => props.claims, [props.claims]);

  const table = useReactTable({
    data: claims,
    columns,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: () => true,
    manualSorting: true,
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 50,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const renderVirtualRows = () => {
    return virtualRows.map((virtualRow) => {
      const row = rows[virtualRow.index];

      return (
        <Fragment key={row.id}>
          <tr className="sm" ref={rowVirtualizer.measureElement} data-index={virtualRow.index}>
            {row.getVisibleCells().map((cell) => {
              return (
                <td key={cell.id} className="t--sm">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              );
            })}
          </tr>
        </Fragment>
      );
    });
  };

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  const [sortState, setSortState] = useState({});

  const sort = (column) => {
    const nextSortingOrder = sortState[column.id] === "asc" ? "desc" : "asc";
    setSortState({ ...sortState, [column.id]: nextSortingOrder });
    props.handleSort(column.id, nextSortingOrder);
  };

  return (
    <>
      <div ref={tableContainerRef} className="virtual__table__reports__container">
        <table className="primary scrollable">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="sm">
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <span
                        style={{ fontSize: 12, display: "inline-flex", verticalAlign: "top" }}
                        onClick={() => sort(header.column)}
                      >
                        <UnfoldMore fontSize="inherit" color="inherit" />
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr>
                <td colSpan={columns.length} style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {renderVirtualRows()}
            {paddingBottom > 0 && (
              <tr>
                <td colSpan={columns.length} style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
